export function getRoomWebUrl(roomId: string): string {
  return `${window.location.origin}/room/${roomId}`;
}

export function getTelegramDeepLink(roomId: string): string | null {
  const botUser = String(import.meta.env.VITE_TELEGRAM_BOT_USERNAME ?? '')
    .trim()
    .replace(/^@/, '');
  const shortName = String(import.meta.env.VITE_TELEGRAM_WEBAPP_SHORT_NAME ?? '').trim();
  if (!botUser || !shortName) return null;
  return `https://t.me/${botUser}/${shortName}?startapp=${encodeURIComponent(roomId)}`;
}

export function getRoomShareUrl(roomId: string): string {
  return getTelegramDeepLink(roomId) ?? getRoomWebUrl(roomId);
}

export function isTelegramWebApp(): boolean {
  const tg = window.Telegram?.WebApp;
  if (!tg) return false;
  if (tg.initData && tg.initData.length > 0) return true;
  return Boolean(tg.platform && tg.platform !== 'unknown');
}

export async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  }
}

export type ShareRoomResult = 'shared' | 'copied' | 'cancelled';

export async function shareRoomLink(opts: {
  roomId: string;
  title: string;
  text: string;
}): Promise<ShareRoomResult> {
  const url = getRoomShareUrl(opts.roomId);
  const tg = window.Telegram?.WebApp;

  if (isTelegramWebApp() && tg?.openTelegramLink) {
    const shareLink = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(opts.text)}`;
    tg.openTelegramLink(shareLink);
    return 'shared';
  }

  if (isTelegramWebApp()) {
    await copyToClipboard(url);
    return 'copied';
  }

  if (navigator.share) {
    try {
      await navigator.share({
        title: opts.title,
        text: opts.text,
        url,
      });
      return 'shared';
    } catch (error) {
      if ((error as Error).name === 'AbortError') return 'cancelled';
    }
  }

  await copyToClipboard(url);
  return 'copied';
}
